import { useEffect, useId, useRef, useState } from "react";
import { api, type Item, type TripView } from "./api.ts";
import { useJustDone } from "./justDone.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button, Icon } from "./souvenir.ts";
import { Flag } from "./Todos.tsx";

// Add a to-do on the web (DESIGN.md, Views, the web's own edits; features/tasks-and-reminders.md, "Add a to-do on the
// web"): a title, for whom (nobody yet, me, anyone in the trip), when it's due and whether it's important, and, from
// a thing's card, what it's about. One write signed "on the web", as an AI's `create` to-do, undoable at once (the
// trip's notice) and from Updates. On Bookings (its button, and its Add's) and on a thing's card.

const MAX_TITLE = 200;

export function AddTodoForm({
  trip,
  about = null,
  onChanged,
  onClose,
}: {
  trip: TripView;
  // The thing it's about (its card), or none (Bookings).
  about?: Item | null;
  onChanged: () => void;
  // Closed, added or not.
  onClose: () => void;
}) {
  const id = useId();
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const [important, setImportant] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    form.current?.querySelector<HTMLElement>("input")?.focus({ preventScroll: true });
    form.current?.scrollIntoView({ block: "nearest" });
  }, []);
  // Me first, then the others by name.
  const people = [...trip.members].sort(
    (a, b) => Number(b.id === trip.you) - Number(a.id === trip.you) || a.name.localeCompare(b.name),
  );

  // Who its due date reminds (features/tasks-and-reminders.md, "Due-date reminders"): its owner, or everyone.
  const whom = owner === trip.you ? "you" : (trip.members.find((m) => m.id === owner)?.name ?? null);
  const reminds = due
    ? `Tripmo reminds ${whom ?? "everyone in the trip"} 3 days before it's due, on the day, and the day after if it's still open.`
    : null;

  const save = async () => {
    if (offline || busy) return;
    const text = title.trim();
    if (!text) {
      setProblem("Say what's to do.");
      form.current?.querySelector<HTMLElement>("input")?.focus();
      return;
    }
    // Only a whole date: a half-typed one is no date.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(due) ? due : null;
    setError(null);
    setBusy(true);
    try {
      const { change } = await api.addTodo(trip.id, {
        title: text,
        owner: owner || null,
        due: date,
        important,
        about: about?.id ?? null,
      });
      onChanged();
      say({ change, text: `Added the to-do “${text.length > 60 ? `${text.slice(0, 59)}…` : text}”` });
      onClose();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Couldn't add the to-do. Try again.");
    }
  };

  return (
    <form
      ref={form}
      className="tm-edit-form tm-add-todo"
      aria-labelledby={`${id}-title`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        // Leaving the form, before the page's own Escape (which closes a card).
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <h2 id={`${id}-title`} className="title">
        Add a to-do
      </h2>
      <div className={`sv-field tm-booking-field${problem ? " is-wrong" : ""}`}>
        <label className="sv-field-label" htmlFor={`${id}-what`}>
          What's to do
        </label>
        <input
          id={`${id}-what`}
          className="sv-field-input"
          value={title}
          maxLength={MAX_TITLE}
          autoComplete="off"
          enterKeyHint="done"
          placeholder={about ? "Buy the tickets, bring cash…" : "Buy JR passes, get an eSIM…"}
          aria-invalid={problem ? true : undefined}
          onChange={(e) => {
            setTitle(e.target.value);
            setProblem(null);
          }}
        />
        {problem && <span className="sv-field-hint tm-error">{problem}</span>}
      </div>
      <div className="tm-add-todo-fields">
        <div className="sv-field tm-booking-field">
          <label className="sv-field-label" htmlFor={`${id}-for`}>
            For
          </label>
          <span className="tm-booking-select">
            <select
              id={`${id}-for`}
              className="sv-field-input"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
            >
              <option value="">Nobody yet</option>
              {people.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === trip.you ? `You (${m.name})` : m.name}
                </option>
              ))}
            </select>
            <span className="tm-booking-chevron" aria-hidden="true">
              <Icon name="chevron" size={16} />
            </span>
          </span>
        </div>
        <div className="sv-field tm-booking-field">
          <label className="sv-field-label" htmlFor={`${id}-due`}>
            Due
          </label>
          <input
            id={`${id}-due`}
            className={`sv-field-input${due ? "" : " is-empty"}`}
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </div>
      </div>
      <label className="tm-todo-important">
        <input type="checkbox" checked={important} onChange={(e) => setImportant(e.target.checked)} />
        <span className="tm-todo-important-text">
          <span className="callout">
            Important <Flag />
          </span>
          <span className="caption tm-muted">
            Everyone in the trip hears of it now, and when it's done, moved, given to someone or removed.
          </span>
        </span>
      </label>
      {reminds && <p className="caption tm-muted">{reminds}</p>}
      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: adding needs signal.</span>
        </p>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      <span className="tm-note-actions">
        <Button variant="primary" size="sm" type="submit" disabled={busy || offline}>
          {busy ? "Adding…" : "Add to-do"}
        </Button>
        <Button variant="quiet" size="sm" type="button" onClick={onClose}>
          Cancel
        </Button>
      </span>
    </form>
  );
}
